import { Link, useNavigate } from "@tanstack/react-router"
import { useQuery } from "convex/react"
import { ChevronDown, LogOut, Settings, Shield } from "lucide-react"
import { api } from "../../convex/_generated/api"
import { toast } from "sonner"
import { authClient, signOutSucceeded } from "@/lib/auth-client"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Skeleton } from "@/components/ui/skeleton"
import { cn, initials } from "@/lib/utils"

function MenuAvatar({
  name,
  imageUrl,
  size = "md",
}: {
  name: string
  imageUrl?: string | null
  size?: "md" | "lg"
}) {
  return (
    <span
      className={cn(
        "mkt-user-menu__avatar",
        size === "lg" && "mkt-user-menu__avatar--lg",
      )}
      aria-hidden
    >
      {imageUrl ? (
        <img src={imageUrl} alt="" className="mkt-user-menu__avatar-img" />
      ) : (
        <span className="mkt-user-menu__avatar-initials">{initials(name)}</span>
      )}
    </span>
  )
}

type DeskUserMenuProps = {
  /** Called after navigating (e.g. close mobile sheet). */
  onNavigate?: () => void
  /** Header trigger vs stacked links for the mobile drawer. */
  variant?: "trigger" | "drawer"
}

export function DeskUserMenu({ onNavigate, variant = "trigger" }: DeskUserMenuProps) {
  const navigate = useNavigate()
  const profile = useQuery(api.profiles.me)
  const { data: session } = authClient.useSession()

  const email = session?.user?.email ?? profile?.email ?? ""
  const name = profile?.name ?? session?.user?.name ?? "Account"
  const avatarUrl = profile?.avatarFileUrl
  const settingsPath =
    profile?.role === "employer" ? "/recruit/settings" : "/candidate/settings"

  const signOut = async () => {
    if (!(await signOutSucceeded())) {
      toast.error("Could not sign out. Please try again.")
      return
    }
    onNavigate?.()
    void navigate({ to: "/" })
  }

  if (profile === undefined) {
    return variant === "trigger" ? (
      <Skeleton className="h-9 w-32 rounded-lg" />
    ) : null
  }

  if (variant === "drawer") {
    return (
      <div className="mkt-user-menu-drawer">
        <div className="mkt-user-menu-drawer__identity">
          <MenuAvatar name={name} imageUrl={avatarUrl} size="lg" />
          <div className="min-w-0">
            <p className="truncate font-semibold text-[color:var(--wf-ink)]">{name}</p>
            {email && (
              <p className="truncate text-sm text-[color:var(--wf-mute)]">{email}</p>
            )}
          </div>
        </div>
        <Link to={settingsPath} className="mkt-nav-link" onClick={onNavigate}>
          Settings
        </Link>
        {profile?.isAdmin && (
          <Link to="/admin" className="mkt-nav-link" onClick={onNavigate}>
            Admin
          </Link>
        )}
        <button type="button" className="mkt-nav-text" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="mkt-user-menu" aria-label="Account menu">
        <MenuAvatar name={name} imageUrl={avatarUrl} />
        <span className="mkt-user-menu__text">
          <span className="mkt-user-menu__name">{name}</span>
          {email && <span className="mkt-user-menu__email">{email}</span>}
        </span>
        <ChevronDown className="mkt-user-menu__chevron" aria-hidden />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={8}
        className="mkt-user-menu__panel"
      >
        <div className="mkt-user-menu__panel-head">
          <MenuAvatar name={name} imageUrl={avatarUrl} size="lg" />
          <div className="min-w-0">
            <p className="mkt-user-menu__panel-name">{name}</p>
            {email && <p className="mkt-user-menu__panel-email">{email}</p>}
          </div>
        </div>
        <div className="mkt-user-menu__panel-body">
          <DropdownMenuItem asChild className="mkt-user-menu__item">
            <Link to={settingsPath} onClick={onNavigate}>
              <Settings className="size-4" aria-hidden />
              Settings
            </Link>
          </DropdownMenuItem>
          {profile?.isAdmin && (
            <DropdownMenuItem asChild className="mkt-user-menu__item">
              <Link to="/admin" onClick={onNavigate}>
                <Shield className="size-4" aria-hidden />
                Admin
              </Link>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            className="mkt-user-menu__item mkt-user-menu__item--signout"
            onClick={() => void signOut()}
          >
            <LogOut className="size-4" aria-hidden />
            Sign out
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
